import React from "react";
import { Box, Typography } from "@mui/material";
import { capitalize } from "lodash";
import { JourneyAnchors } from "../../../constants";

interface IToggleButtonGroup<T> {
  value: T;
  setValue: (value: T) => void;
  availableValues?: T[];
}

const ToggleButtonGroup = function <T>({
  value,
  setValue,
  availableValues = [],
}: IToggleButtonGroup<T>) {
  const isSingle = availableValues.length === 1;

  if (isSingle) {
    return (
      <Typography
        variant="body2"
        sx={{ fontWeight: 700, color: "text.primary" }}
      >
        {capitalize(String(availableValues[0]))}
      </Typography>
    );
  }

  return (
    <Box
      sx={{ display: "flex" }}
      className={JourneyAnchors.EXERCISE_TYPE_TOGGLE}
    >
      {availableValues.map((type, index) => {
        const isSelected = value === type;
        const isFirst = index === 0;
        const isLast = index === availableValues.length - 1;

        return (
          <Box
            component="button"
            key={String(type)}
            onClick={() => setValue(type)}
            sx={{
              flex: 1,
              display: "flex",
              minWidth: 90,
              justifyContent: "center",
              alignItems: "center",
              px: 1.5,
              height: 40,
              border: 1,
              borderColor: "divider",
              cursor: "pointer",
              bgcolor: isSelected
                ? (theme) => theme.palette.action.selected
                : "transparent",

              "&:hover": {
                backgroundColor: (theme) => theme.palette.action.hover,
              },

              ...(isFirst && {
                borderTopLeftRadius: (theme) => theme.spacing(1),
                borderBottomLeftRadius: (theme) => theme.spacing(1),
                borderRight: 0,
              }),

              ...(isLast && {
                borderTopRightRadius: (theme) => theme.spacing(1),
                borderBottomRightRadius: (theme) => theme.spacing(1),
                borderLeft: 0,
              }),

              ...(!isFirst &&
                !isLast && {
                  borderLeft: 0,
                  borderRight: 0,
                }),
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: isSelected ? "primary.main" : "text.primary",
              }}
            >
              {capitalize(String(type))}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default ToggleButtonGroup;
